import type { CSSProperties } from 'react'
import type { RoomPlan } from '../schema/elements'
import {
  roomPlanSize,
  roomPlanViewBox,
  shouldDrawSeats,
  tableCentre,
  tablePickBoxes
} from '../utils/roomPlan'
import { SEAT_RADIUS, getSeatPositions } from './seatLayout'

interface RoomPlanPick {
  /** The form field every table's radio belongs to. */
  name: string
  /**
   * What the radios' ids start with, so that two blocks showing the same room
   * on one page do not share an id between them.
   */
  idPrefix: string
  /** "Table", in the page's language, for the name a screen reader gives each. */
  tableWord: string
}

interface RoomPlanDrawingProps {
  room: RoomPlan
  /** Read out to somebody who cannot see the drawing. */
  title: string
  /**
   * When given, every table is a choice: a radio laid over it, in this form
   * field. Without it the drawing is a picture, which is what a page whose
   * booking has been switched off still shows.
   */
  pick?: RoomPlanPick
}

/**
 * The venue's own room, drawn on the public page.
 *
 * SVG and not the planner's canvas, and that is not a preference. The planner
 * draws with `react-konva`, which needs a browser and a canvas element; this
 * page is rendered to static HTML by `renderPage`, in Node, with no DOM at all.
 * The same numbers describe both pictures, so the drawing is the same picture.
 *
 * Nothing here carries a colour or a face of its own. Every one of them arrives
 * as a custom property on the page (`site.scss`), which is what makes the room
 * follow whichever way the owner set the look: derived from the one colour in
 * the template, or set role by role in the builder. Neither path needs to know
 * this drawing exists.
 */
function RoomPlanDrawing({ room, title, pick }: RoomPlanDrawingProps) {
  if (room.tables.length === 0) return null

  const seats = shouldDrawSeats(room)
  const box = roomPlanViewBox(room, { seats })

  return (
    /*
      A wrapper around the drawing, sized like the drawing used to be, because
      the picker has to lie exactly over it: an HTML label per table, placed by
      percentages of this box, which follow the drawing whatever size the page
      gives it. Inline geometry, computed from the plan, is the one kind of
      inline style CLAUDE.md allows.
    */
    <div className="mp-room__plan">
      <svg
        className="mp-room__drawing"
        viewBox={`${box.x} ${box.y} ${box.width} ${box.height}`}
        /*
          The room keeps its proportions and sits in the middle of whatever
          width the page gives it. Stretched to fit, a long narrow room would
          arrive as a square and stop being a picture of anywhere.
        */
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={title}
      >
        {room.tables.map(table => {
          const centre = tableCentre(table)
          return (
            <g
              key={table.id}
              transform={`translate(${centre.x} ${centre.y}) rotate(${table.rotation})`}
            >
              {/*
                The chairs first, so a table drawn over them hides the edge that
                would otherwise be a hairline between the two.

                Inside the turned group, so they turn with the table. The waiter
                app leaves rotation out of its plan entirely; the planner's own
                canvas does not, and this is the picture that has to match the
                planner.
              */}
              {seats &&
                getSeatPositions(
                  table.shape,
                  table.seatCount,
                  table.width,
                  table.height
                ).map(seat => (
                  <circle
                    key={`${table.id}-${seat.x}-${seat.y}`}
                    className="mp-room__seat"
                    cx={seat.x}
                    cy={seat.y}
                    r={SEAT_RADIUS}
                  />
                ))}
              {table.shape === 'round' ? (
                <circle className="mp-room__table" r={table.width / 2} />
              ) : (
                <rect
                  className="mp-room__table"
                  x={-table.width / 2}
                  y={-table.height / 2}
                  width={table.width}
                  height={table.height}
                  rx={table.shape === 'square' ? 4 : 6}
                />
              )}
              {/*
                Turned back the other way, so a table standing at an angle still
                has a number somebody can read. The planner does the same thing
                with the same minus sign.
              */}
              <g transform={`rotate(${-table.rotation})`}>
                <text
                  className="mp-room__label"
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {table.label}
                </text>
              </g>
            </g>
          )
        })}
      </svg>
      {pick &&
        tablePickBoxes(room).map(table => (
          <label
            key={`pick-${table.id}`}
            className={`mp-room__pick mp-room__pick--${table.shape}`}
            htmlFor={`${pick.idPrefix}-${room.id}-${table.id}`}
            style={{
              left: `${table.left}%`,
              top: `${table.top}%`,
              width: `${table.width}%`,
              height: `${table.height}%`,
              transform: table.rotation ? `rotate(${table.rotation}deg)` : undefined
            }}
          >
            <input
              className="mp-room__pick-input mp-sheet__opener"
              id={`${pick.idPrefix}-${room.id}-${table.id}`}
              type="radio"
              name={pick.name}
              value={`${room.id}/${table.id}`}
              required
            />
            <span className="mp-visually-hidden">
              {pick.tableWord} {table.label}
            </span>
          </label>
        ))}
    </div>
  )
}

/**
 * The sizes the frame around a drawing is styled by, as custom properties.
 *
 * On the FRAME and not on the drawing, because the frame is what needs them
 * first: it takes the plan's proportions with `aspect-ratio`, which has to be
 * known before the drawing inside it is laid out. Custom properties inherit,
 * so the drawing reads the same three values a level down.
 *
 * Unitless, because `aspect-ratio` wants two plain numbers and cannot take
 * pixels; the stylesheet multiplies by `1px` where it needs a length. The
 * numbers come from the plan, which is the one thing a stylesheet cannot know.
 */
export function roomPlanFrameStyle(room: RoomPlan): CSSProperties {
  const size = roomPlanSize(room)
  return {
    '--mp-room-w': `${size.width}`,
    '--mp-room-h': `${size.height}`,
    '--mp-room-min': `${size.minWidth}`
  } as CSSProperties
}

export default RoomPlanDrawing
